const GATEWAY = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

export interface AuthUser {
  id: string
  /** null for an account created by SMS OTP — that flow never asks for one. */
  email: string | null
  /** E.164 (+91XXXXXXXXXX). Only set on accounts that have signed in by SMS. */
  phone?: string | null
  display_name: string
  is_verified: number
  role: 'customer' | 'admin'
}

export interface AuthResponse {
  accessToken: string
  user: AuthUser
}

// /auth/google alone reports whether this request CREATED the account. Kept as
// its own type rather than an optional field on AuthResponse, so nothing can
// read `is_new` off a /login or /signup response, where the server never sends
// it and `undefined` would silently read as "returning user".
export interface GoogleAuthResponse extends AuthResponse {
  /** True only when the account was created by this sign-in. */
  is_new: boolean
}

export async function loginApi(email: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${GATEWAY}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',  // ← sends/receives httpOnly cookie
    body: JSON.stringify({ email, password })
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Login failed')
  }
  return res.json()
}

export async function signupApi(
  email: string,
  password: string,
  display_name: string
): Promise<AuthResponse> {
  const res = await fetch(`${GATEWAY}/api/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, display_name })
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Signup failed')
  }
  return res.json()
}

// "Sign in with Google". `credential` is the ID token (JWT) handed to us by
// Google Identity Services in the browser — we forward it untouched for
// server-side verification; it is never decoded or trusted client-side.
export async function googleAuthApi(credential: string): Promise<GoogleAuthResponse> {
  const GENERIC = 'Google sign-in failed. Please try again.'
  let res: Response
  try {
    res = await fetch(`${GATEWAY}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential })
    })
  } catch {
    // Network / CORS failure — never surface the raw fetch error.
    throw new Error(GENERIC)
  }
  if (!res.ok) {
    // The body may be a JSON { error } from our services, OR a non-JSON page
    // (e.g. a 404/502 HTML page from the gateway). Parsing that with res.json()
    // throws "Unexpected token '<' … is not valid JSON", so guard it and fall
    // back to a generic message rather than leaking the parser error.
    const message = await res.json().then(d => d?.error).catch(() => null)
    throw new Error(message || GENERIC)
  }
  try {
    return await res.json()
  } catch {
    throw new Error(GENERIC)
  }
}

// ── SMS OTP login ────────────────────────────────────────────────────────────
// Two steps, one number. Both reject by THROWING the parsed error body rather
// than an Error: OTPLoginForm needs `attemptsRemaining` off a wrong-code reply
// to say "2 attempts remaining", and an Error can only carry a message.

/**
 * What both OTP endpoints reject with.
 *
 * A real Error subclass rather than a thrown object literal: it still exposes
 * `.error` and `.attemptsRemaining` for the form to read, but it also carries a
 * stack and satisfies `instanceof Error`, so anything that catches it
 * generically (an error boundary, a logger) behaves.
 */
export class OtpApiError extends Error {
  /** The user-facing message from the server. */
  readonly error: string
  /** Only on an incorrect code: guesses left before this OTP is spent. */
  readonly attemptsRemaining?: number
  /** HTTP status, so the caller can tell 429 (rate limited) from 400. */
  readonly status?: number

  constructor(error: string, opts: { attemptsRemaining?: number; status?: number } = {}) {
    super(error)
    this.name = 'OtpApiError'
    this.error = error
    this.attemptsRemaining = opts.attemptsRemaining
    this.status = opts.status
  }
}

const OTP_GENERIC = 'Something went wrong. Please try again.'

/** Parse a failed OTP response into the object callers throw/catch. */
async function otpFailure(res: Response): Promise<OtpApiError> {
  // The body may be a JSON { error } from our services OR a non-JSON page (a
  // 502 from the gateway). Parsing that with res.json() throws, so guard it.
  const body = await res.json().catch(() => null) as
    { error?: string; attemptsRemaining?: number } | null
  return new OtpApiError(body?.error || OTP_GENERIC, {
    attemptsRemaining: body?.attemptsRemaining,
    status: res.status,
  })
}

/**
 * Step 1 — ask for a code. `phone` is the raw 10 digits; the server normalizes
 * and is the only place that decides what a valid Indian mobile is.
 */
export async function sendOTP(phone: string): Promise<{ success: boolean; message: string }> {
  let res: Response
  try {
    res = await fetch(`${GATEWAY}/api/auth/otp/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone })
    })
  } catch {
    // Network / CORS failure — never surface the raw fetch error.
    throw new OtpApiError('Could not reach the server. Check your connection.')
  }
  if (!res.ok) throw await otpFailure(res)
  return res.json()
}

/** Step 2 — exchange a correct code for a session (same tokens as /login). */
export async function verifyOTP(
  phone: string,
  code: string
): Promise<AuthResponse & { is_new: boolean }> {
  let res: Response
  try {
    res = await fetch(`${GATEWAY}/api/auth/otp/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone, code })
    })
  } catch {
    throw new OtpApiError('Could not reach the server. Check your connection.')
  }
  if (!res.ok) throw await otpFailure(res)
  try {
    return await res.json()
  } catch {
    throw new OtpApiError(OTP_GENERIC)
  }
}

/**
 * In-flight refresh, shared by every caller.
 *
 * Refresh tokens ROTATE, and the server treats a second use of an already
 * rotated token as theft: it revokes the whole token family, which logs the
 * user out for real. So two concurrent refreshes are not merely wasteful — the
 * first succeeds, the second presents the token the first just invalidated, and
 * the session is destroyed. Module scope, not a hook, so the guard holds across
 * every caller in the app rather than per component.
 */
let refreshInFlight: Promise<{ accessToken: string } | null> | null = null

export async function refreshApi(): Promise<{ accessToken: string } | null> {
  // Whoever asked second rides on the first request's answer.
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = doRefresh().finally(() => { refreshInFlight = null })
  return refreshInFlight
}

async function doRefresh(): Promise<{ accessToken: string } | null> {
  try {
    const res = await fetch(`${GATEWAY}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include'  // ← sends refresh cookie automatically
    })
    if (!res.ok) return null
    return res.json()
  } catch {
    return null
  }
}

export async function logoutApi(): Promise<void> {
  await fetch(`${GATEWAY}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include'
  })
}
